import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    inject,
    ModalHandle,
    NothingHere,
    TableHeadOptions,
    SubTopBar,
    Title,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { DropdownItem, DropdownMenu, DropdownToggle, Input as RInput, UncontrolledButtonDropdown } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { InviteModal } from './InviteModal';
import { InviteRow } from './InviteRow';
import { InvitesStore } from './InvitesStore';
import { BsFillEnvelopeFill } from '@react-icons/all-files/bs/BsFillEnvelopeFill';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';

export interface IInvitesListProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class InvitesList extends React.Component<IInvitesListProps> {
    public componentDidMount() {
        this.loadDataAsync();
    }

    public render() {
        return (
            <div className="container-fluid">
                <Title>Invitations</Title>

                <SubTopBar>
                    <RInput
                        type="text"
                        placeholder="Search Invites..."
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                    />

                    {this.accountStore.isInRoles(['MriAdmin']) && (
                        <UncontrolledButtonDropdown>
                            <DropdownToggle caret color="danger" size="sm" className="mx-2">
                                <BsTrash className="" />
                            </DropdownToggle>
                            <DropdownMenu end className="p-0 dropdown-padding">
                                <DropdownItem className="text-danger fw-bold py-0" onClick={this.deleteAll}>
                                    Delete All
                                </DropdownItem>

                                <DropdownItem className="text-danger fw-bold py-0" onClick={this.deleteAllExpired}>
                                    Delete All Expired
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>
                    )}

                    <button className="btn btn-primary ms-auto" onClick={this.openModal}>
                        Invite User
                    </button>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 p-0">
                        <table className="table-striped w-100">
                            <thead className="bg-color">
                                <tr>
                                    <th className="px-2 py-3">
                                        Email
                                        <TableHeadOptions
                                            value={this.sort}
                                            field="userName"
                                            onChange={this.onSortChange}
                                            exclusive
                                        />
                                    </th>
                                    <th>
                                        Expires
                                        <TableHeadOptions
                                            value={this.sort}
                                            field="expiresAt"
                                            onChange={this.onSortChange}
                                            exclusive
                                        />
                                    </th>
                                    <th>Roles</th>
                                    <th>Accounts</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {this.invites.map(i => (
                                    <InviteRow key={i.invitationId} invitation={i} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <NothingHere
                    empty={!this.invites.length}
                    icon={<BsFillEnvelopeFill />}
                    loading={this.loading}
                    label="No Invitations Available"
                    loadingMessage="Loading Invitations..."
                />

                <InviteModal modalHandle={this.modalHandle} />
            </div>
        );
    }

    @bind
    @action
    private openModal() {
        this.modalHandle.open();
    }

    @bind
    @action
    private async loadDataAsync() {
        this.loading = true;

        try {
            this.invitesStore.invitations.clear();

            await this.invitesStore.loadInvitationsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @bind
    @action
    private async onSortChange(value: string[]) {
        this.sort = value;
    }

    @bind
    @action
    private async deleteAll() {
        this.loading = true;

        try {
            await this.invitesStore.deleteAllAsync({ params: { onlyExpired: false } });
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async deleteAllExpired() {
        this.loading = true;

        try {
            await this.invitesStore.deleteAllAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @computed
    private get invites() {
        let invites = toArray(this.invitesStore.invitations);

        if (this.searchValue)
            invites = invites!.filter(i => {
                return deepStringFind(i, this.searchValue);
            });
        if (this.sort.length) {
            for (const s of this.sort) {
                const [k, d] = s.split(' ');

                invites = invites.sort((a: any, b: any) => {
                    const v1 = a[k];
                    const v2 = b[k];
                    if (v1 < v2) return -1;
                    if (v1 > v2) return 1;
                    return 0;
                });
                if (d == 'desc') {
                    invites = invites.reverse();
                }
            }
        }
        return invites;
    }

    @observable
    private loading: boolean = false;
    private modalHandle = new ModalHandle();
    @observable
    private sort: string[] = [];
    @observable
    private searchValue: string = '';
    @inject
    private invitesStore!: InvitesStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
}
